import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Megaphone, FileText } from "lucide-react";
import api from "../lib/api";
import { IMAGES, FALLBACK_IMAGE } from "../lib/images";
import { Reveal, useSEO } from "../components/site/Reveal";
import Newsletter from "../components/site/Newsletter";

const NEWS_CATEGORIES = ["news", "statement", "statements", "press", "announcement"];

export default function News() {
  useSEO(
    "News & Statements",
    "The latest news, announcements, and official statements from ÀsÀNigHT Worldwide."
  );
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    api
      .get("/articles")
      .then((r) => setItems(r.data.filter((a) => NEWS_CATEGORIES.includes((a.category || "").toLowerCase()))))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <header className="relative bg-forest text-cream grain-overlay">
        <div className="absolute inset-0 opacity-[0.12] bg-cover bg-center" style={{ backgroundImage: `url(${IMAGES.adirePattern})` }} />
        <div className="relative max-w-5xl mx-auto px-6 py-24 text-center">
          <p className="uppercase tracking-[0.3em] text-xs text-gold-soft mb-5">Stay Informed</p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-serif text-5xl lg:text-6xl leading-tight"
          >
            News & Statements
          </motion.h1>
          <p className="mt-5 text-cream/75 text-lg max-w-2xl mx-auto">
            Official announcements, updates, and statements from ÀsÀNigHT Worldwide.
          </p>
        </div>
      </header>

      <section className="max-w-5xl mx-auto px-6 py-20 min-h-[40vh]">
        {loading ? (
          <p className="text-center text-ink-muted">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-center text-ink-muted" data-testid="news-empty-state">No news or statements yet. Check back soon.</p>
        ) : (
          <div className="space-y-6">
            {items.map((a, i) => {
              const isStatement = (a.category || "").toLowerCase().startsWith("statement");
              const date = new Date(a.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
              return (
                <Reveal key={a.id} delay={(i % 4) * 0.05}>
                  <Link
                    to={`/blog/${a.slug}`}
                    data-testid={`news-card-${a.slug}`}
                    className="group grid sm:grid-cols-3 gap-0 border border-parchment rounded-xl overflow-hidden bg-cream hover:border-gold transition-colors"
                  >
                    <div className="sm:col-span-1 h-48 sm:h-full overflow-hidden">
                      <img src={a.cover_image || FALLBACK_IMAGE} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="sm:col-span-2 p-7">
                      <div className="flex items-center gap-2 mb-3">
                        <span className={`inline-flex items-center gap-1.5 text-xs uppercase tracking-widest rounded-full px-3 py-1 ${isStatement ? "bg-forest text-cream" : "bg-gold/15 text-gold"}`}>
                          {isStatement ? <FileText className="w-3 h-3" /> : <Megaphone className="w-3 h-3" />}
                          {a.category}
                        </span>
                        <span className="text-xs text-ink-muted">{date}</span>
                      </div>
                      <h2 className="font-serif text-2xl md:text-3xl text-ink leading-snug group-hover:text-forest transition-colors mb-2">{a.title}</h2>
                      <p className="text-ink-muted leading-relaxed line-clamp-2">{a.excerpt}</p>
                      <span className="inline-block mt-4 text-sm font-semibold text-gold">Read more →</span>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        )}
      </section>

      <Newsletter />
    </div>
  );
}
